/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { memo, useCallback } from "react";
import { HeaderProps } from "./IHeader";

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: "solid",
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderColor: theme.palette.divider,
  boxShadow: "none",
  zIndex: theme.zIndex.drawer + 1,
}));

const Header: React.FC<HeaderProps> = ({
  toggleNavigationExpanded,
  disableCollapsibleSidebar,
  hideNavigation,
  toolbarView,
  isMobileNavigationExpanded,
  isDesktopNavigationExpanded,
  sxStyle,
}) => {
  const ToolbarView = toolbarView ?? null;
  const getMenuIcon = useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = "Expand";
      const collapseMenuActionText = "Collapse";

      return (
        <Tooltip
          title={`${isExpanded ? collapseMenuActionText : expandMenuActionText} menu`}
          enterDelay={1000}
        >
          <div>
            <IconButton
              aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} navigation menu`}
              onClick={toggleNavigationExpanded}
            >
              {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    [toggleNavigationExpanded],
  );

  return (
    <AppBarStyled position="absolute" sx={{ displayPrint: "none", ...sxStyle }}>
      <Toolbar sx={{ backgroundColor: "inherit", mx: { xs: -0.75, sm: -1.5 } }}>
        {!hideNavigation ? (
          <>
            <Box
              sx={{
                mr: { sm: disableCollapsibleSidebar ? 0 : 1 },
                display: { md: "none" },
              }}
            >
              {getMenuIcon(isMobileNavigationExpanded)}
            </Box>
            <Box
              sx={{
                display: {
                  xs: "none",
                  md: disableCollapsibleSidebar ? "none" : "block",
                },
                mr: disableCollapsibleSidebar ? 0 : 1,
              }}
            >
              {getMenuIcon(isDesktopNavigationExpanded)}
            </Box>
          </>
        ) : null}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          {ToolbarView && ToolbarView}
        </Stack>
      </Toolbar>
    </AppBarStyled>
  );
};

export default memo(Header);
